// import { UserActivityCategory } from '@pascal/iframe-service';
import React, { useCallback, useMemo, useState } from 'react';
import { UserActivityCategory, UserActivityTypes } from '@pascal/iframe-service';
import { observer } from 'mobx-react-lite';

import { GameRedirectionSource, PopUpActionType } from '../../../../enums';
import { useOutsideClick, useStore, useTranslation } from '../../../../hooks';
import { PopupLS, Popup as PopupModel } from '../../../../models';
import { integration } from '../../../../services';
import { isMobile } from '../../../../helpers';
import { env } from '../../../../environment';
import { T } from '../../../../constants';

import './style.scss';

const DONT_SHOW_POPUPS_LS_KEY = 'dontShowPopups';

interface PopupProps {
  popups: Array<PopupModel>;
}

export const Popup: React.FC<PopupProps> = observer(({
  popups
}) => {

  const { modalStore, gameStore, userActivityStore } = useStore();
  const { gameReady } = gameStore;

  const t = useTranslation();

  const [currentPopupIndex, setCurrentPopupIndex] = useState(0);
  const [dontShowAgainChecked, setDontShowAgainChecked] = useState(false);
  const [dontShowAgainPopups, setDontShowAgainPopups] = useState<Array<PopupLS>>(JSON.parse(localStorage.getItem(DONT_SHOW_POPUPS_LS_KEY) || '[]') as Array<PopupLS> || []);

  const dontShowAgainPopupsMap = useMemo(() => {
    return dontShowAgainPopups.reduce<Record<number, boolean>>((acc, curr) => {
      acc[curr.id] = true;
      return acc;
    }, {});
  }, [dontShowAgainPopups]);

  // Each entry normally ships all three renditions (web / mobile / banner) of the same popup —
  // the banner one is what SidebarMenuHeader picks up. Guard anyway: without popup artwork there
  // is nothing to render but the chrome.
  const visiblePopups = useMemo(() => {
    return popups.filter(({ id, webUrl, mobileUrl }) => (!!webUrl || !!mobileUrl) && !dontShowAgainPopupsMap[id])
  }, [popups, dontShowAgainPopupsMap]);

  const currentPopup = visiblePopups[currentPopupIndex] as PopupModel | undefined;

  const isGameRedirection = currentPopup?.actionType === PopUpActionType.GameRedirection;

  const imagePath = isMobile()
    ? currentPopup?.mobileUrl || currentPopup?.webUrl
    : currentPopup?.webUrl || currentPopup?.mobileUrl;

  // the artwork is the popup design itself, so it replaces the placeholder background declared in
  // style.scss; an inline rule outranks the [class*="is-web"] / [class*="is-mobile"] selectors there
  const backgroundStyle = imagePath
    ? { backgroundImage: `url(${env.filesUrl}/${imagePath})` }
    : undefined;

  const setPopupsToLocalStorage = useCallback((dontShowAgainPopups: Array<PopupLS>) => {
    localStorage.setItem(DONT_SHOW_POPUPS_LS_KEY, JSON.stringify(dontShowAgainPopups));
  }, []);

  const rememberDontShowAgain = useCallback((popupId: number) => {
    setDontShowAgainPopups((prev) => {
      const updatedData = [...prev, { id: popupId, viewDate: new Date() }];
      setPopupsToLocalStorage(updatedData);
      return updatedData;
    });
  }, [setPopupsToLocalStorage]);

  const closeFeature = useCallback(() => {
    modalStore.setPopupFeatureOpen(false);
    setCurrentPopupIndex(-1);
  }, [modalStore]);

  /**
   * @description shared dismissal path for the action button, the X button and the outside click.
   * Honours "don't show again" whichever way the popup was dismissed, then advances the queue —
   * when `dontShowAgainChecked` the current popup drops out of `visiblePopups`, so the index
   * already points at the next one and must not be incremented.
   */
  const dismissCurrentPopup = useCallback(() => {
    if (!currentPopup) return;
    // `visiblePopups` is still the pre-dismissal memo, so the queue has to be projected forward here.
    // Reading the stale list instead would leave `popupFeatureOpen` true after the last popup was
    // dismissed with the checkbox ticked, and `anyFeaturePopupOpen` would suppress the other features.
    const remaining = dontShowAgainChecked
      ? visiblePopups.filter(({ id }) => id !== currentPopup.id)
      : visiblePopups;
    const nextIndex = dontShowAgainChecked ? currentPopupIndex : currentPopupIndex + 1;
    if (dontShowAgainChecked) rememberDontShowAgain(currentPopup.id);
    if (remaining[nextIndex]) {
      setCurrentPopupIndex(nextIndex);
      setDontShowAgainChecked(false);
      return;
    }
    closeFeature();
  }, [currentPopup, visiblePopups, currentPopupIndex, dontShowAgainChecked, rememberDontShowAgain, closeFeature]);

  const handleClose = useCallback(() => {
    if (!currentPopup) return;
    userActivityStore.sendTrackUserActivity({
      category: UserActivityCategory.PromoTools,
      type: UserActivityTypes.PopupClose,
      data: currentPopup.id
    });
    dismissCurrentPopup();
  }, [currentPopup, userActivityStore, dismissCurrentPopup]);

  const handleActionClick = useCallback(() => {
    if (!currentPopup) return;
    userActivityStore.sendTrackUserActivity({
      category: UserActivityCategory.PromoTools,
      type: UserActivityTypes.PopupButton,
      data: currentPopup.id
    });
    const gameId = currentPopup.redirectionGameId;
    // "Play Now" leaves for another game, so the whole queue is dropped instead of advanced
    if (isGameRedirection && gameId) {
      if (dontShowAgainChecked) rememberDontShowAgain(currentPopup.id);
      closeFeature();
      integration.current.changeGame({ gameId, source: GameRedirectionSource.Popup });
      return;
    }
    dismissCurrentPopup();
  }, [currentPopup, isGameRedirection, dontShowAgainChecked, userActivityStore, rememberDontShowAgain, closeFeature, dismissCurrentPopup]);

  const { modalRef, containerRef } = useOutsideClick(handleClose);

  if (!currentPopup || !gameReady) return null;

  return (
    <div className="popup-container-wrp" ref={containerRef}>
      <div className="popup-container-space">
        <div className="popup-wr" ref={modalRef} style={backgroundStyle}>
          <div className="free-drop-popup-live-close" onClick={handleClose}>
            <i className="icon-close free-drop-popup-close-icon" />
          </div>
          <div className="popup-wr-inner-dont">
            <div className="popup-wr-logo-block" />
            <div className="popup-footer">
              <button
                type="button"
                onClick={handleActionClick}
                className="popup-ok-button"
              >
                {t(isGameRedirection ? T.PLAY_NOW : T.OK)}
              </button>
              <div className="popup-footer-message">
                <div className="checkbox-wrapper">
                  <input
                    type="checkbox"
                    className="checkbox-input"
                    checked={dontShowAgainChecked}
                    id="dontShowAgainPopupCheckbox"
                    onChange={(event) => setDontShowAgainChecked(event.target.checked)}
                  />
                  <label className="input-label" htmlFor="dontShowAgainPopupCheckbox">
                    <span className="label-check">
                      <svg width="10px" height="9px" viewBox="0 0 10 9">
                        <polyline points="1 5 4 8 11 1"></polyline>
                      </svg>
                    </span>
                    <span className="popup-footer-title">{t(T.DONT_SHOW_AGAIN)}</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});
